import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { InternalFieldName } from 'react-hook-form/dist/types/fields';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Form from '../Crud/Form';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';

export interface Role {
  label: string;
  value: string;
}

export interface UserFormFields {
  name: string;
  role: string;
  email: string;
  paymentValue?: number;
  plainPassword?: string;
}

interface UserFormProps {
  isLoading: boolean;
  isUpdate?: boolean;
  title: string;
  formFields: UserFormFields;
  formErrors: Record<string, any>;
  onSubmit: (data: any) => void;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function UserForm({
  isLoading,
  isUpdate = false,
  title,
  formFields,
  formErrors,
  onSubmit,
}: UserFormProps) {
  const {
    user: { organization },
  } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<UserFormFields>();

  async function getRoles() {
    if (organization) {
      const { data } = await api.get(
        `admin/organizations/${organization.id}/users/available-roles`
      );

      setRoles(data.data);
    }
  }

  useEffect(() => {
    getRoles();
  }, [organization]);

  useEffect(() => {
    reset(formFields);
  }, [formFields]);

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  return (
    <Form isLoading={isLoading} title={title} onSubmitCallback={() => {}}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='mb-3 row'>
          <div className='col-md-6'>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor='inputLabel' className='form-label'>
              Nome
            </label>
            <input
              {...register('name', { required: true })}
              className='form-control'
              placeholder='Nome'
              type='text'
              required
            />
            {errors.name && (
              <div className='form-text text-danger'>{errors.name.message}</div>
            )}
          </div>
          <div className='col-md-6'>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className='form-label'>Selecione o perfil</label>
            <select
              required
              className='form-select'
              aria-label='Selecione o perfil'
              {...register('role', { required: true })}
            >
              <option value=''>Selecione o perfil</option>
              {roles.map((role) => (
                <option value={role.value} key={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            {errors.role && (
              <div className='form-text text-danger'>{errors.role.message}</div>
            )}
          </div>
        </div>
        <div className='row'>
          <div className='col-md-6'>
            <div className='mb-3'>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor='inputLabel' className='form-label'>
                E-mail
              </label>
              <input
                {...register('email', { required: true })}
                className='form-control'
                placeholder='E-mail'
                type='email'
                required
              />
              {errors.email && (
                <div className='form-text text-danger'>
                  {errors.email.message}
                </div>
              )}
            </div>
          </div>
          <div className='col-md-6'>
            <div className='mb-3'>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor='inputLabel' className='form-label'>
                Senha
              </label>
              <div className='input-group flex-nowrap'>
                <span
                  className='input-group-text'
                  onClick={() => setShowPassword(!showPassword)}
                  role='button'
                  aria-hidden='true'
                >
                  {showPassword && <FaEyeSlash />}
                  {!showPassword && <FaEye />}
                </span>
                <input
                  {...register('plainPassword', { required: !isUpdate })}
                  className='form-control'
                  placeholder='Senha'
                  type={showPassword ? 'text' : 'password'}
                  autoComplete='new-password'
                  required={!isUpdate}
                />
              </div>
              {isUpdate && (
                <div className='form-text'>
                  Deixe em branco para manter a senha atual
                </div>
              )}
              {errors.plainPassword && (
                <div className='form-text text-danger'>
                  {errors.plainPassword.message}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='d-flex justify-content-end gap-2'>
          <Link to='/admin/users' className='btn btn-secondary'>
            Cancelar
          </Link>
          <button type='submit' className='btn btn-primary'>
            Salvar
          </button>
        </div>
      </form>
    </Form>
  );
}
